import { supabase, Profile } from '../lib/supabase'

// Helper to get a valid access token, refreshing if necessary
async function getValidAccessToken(): Promise<string | null> {
  // First try to get current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession()
  
  console.log('[Auth Debug] getSession result:', {
    hasSession: !!session,
    sessionError,
    expiresAt: session?.expires_at,
    expiresAtDate: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
    now: new Date().toISOString(),
    tokenPreview: session?.access_token ? session.access_token.substring(0, 20) + '...' : null,
  })
  
  if (!session) {
    console.log('[Auth Debug] No session found, returning null')
    return null
  }
  
  // Check if token is expired or about to expire (within 60 seconds)
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
  const isExpiringSoon = expiresAt - Date.now() < 60000
  const isExpired = expiresAt < Date.now()
  
  console.log('[Auth Debug] Token status:', {
    expiresAt,
    now: Date.now(),
    diff: expiresAt - Date.now(),
    isExpired,
    isExpiringSoon,
  })
  
  if (isExpiringSoon || isExpired) {
    console.log('[Auth Debug] Token expired or expiring soon, attempting refresh...')
    // Try to refresh the session
    const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession()
    console.log('[Auth Debug] refreshSession result:', {
      hasRefreshedSession: !!refreshedSession,
      refreshError: error,
      newExpiresAt: refreshedSession?.expires_at,
    })
    if (error || !refreshedSession) {
      console.warn('[Auth Debug] Failed to refresh session:', error)
      // Don't sign out immediately - only sign out if refresh token is truly invalid
      // For network errors or temporary issues, return the existing token anyway
      // The server will handle validation
      if (error?.message?.includes('Invalid Refresh Token') || 
          error?.message?.includes('Refresh Token Not Found')) {
        console.warn('[Auth Debug] Refresh token invalid, signing out user')
        await supabase.auth.signOut()
        return null
      }
      // For other errors (network issues, etc.), try using the existing token
      // It might still work or the server will reject it
      console.log('[Auth Debug] Using existing token despite refresh failure')
      return session.access_token
    }
    return refreshedSession.access_token
  }
  
  return session.access_token
}

// Helper to make authenticated requests to Edge Functions
async function fetchEdgeFunction<T>(
  functionName: string,
  options: {
    method?: string
    body?: Record<string, unknown>
    params?: Record<string, string>
  } = {}
): Promise<T> {
  const { method = 'GET', body, params } = options
  
  // Get valid access token (refreshing if necessary)
  const accessToken = await getValidAccessToken()
  
  // Build the full URL with query params
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  let url = `${supabaseUrl}/functions/v1/${functionName}`
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }
  
  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': anonKey,
  }
  
  // Only add Authorization header if we have a valid access token
  // If no access token, request will be treated as anonymous
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  
  console.log('[API Debug] Making request:', {
    url,
    method,
    hasAuth: !!accessToken,
  })
  
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  
  const data = await response.json()
  
  console.log('[API Debug] Response:', {
    status: response.status,
    ok: response.ok,
    data,
  })
  
  if (!response.ok) {
    // If we get 401 with specific JWT errors, try to refresh the session first
    if (response.status === 401) {
      const errorMessage = data.message || data.error || ''
      console.warn('[API Debug] 401 error:', errorMessage)
      
      // Only sign out for truly invalid JWT errors, not for temporary issues
      if (errorMessage === 'Invalid JWT' || 
          errorMessage.includes('JWT expired') ||
          errorMessage.includes('invalid signature')) {
        // Try to refresh the session one more time before signing out
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError || !refreshedSession) {
          console.warn('[API Debug] Session refresh failed after 401, signing out user')
          await supabase.auth.signOut()
        } else {
          console.log('[API Debug] Session refreshed after 401, please retry the request')
        }
      }
    }
    throw new Error(data.error || data.message || 'Request failed')
  }
  
  return data as T
}

// Auth API - uses Supabase Auth directly
export const authApi = {
  loginWithGitHub: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
    return data
  },

  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
    return { data, needsConfirmation: !data.session }
  },

  signInWithEmail: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  },

  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    if (error) throw error
  },

  updatePassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })
    if (error) throw error
  },
  
  logout: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },
  
  getSession: () => supabase.auth.getSession(),
  
  getUser: () => supabase.auth.getUser(),
  
  getProfile: async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (error) {
      console.error('Error fetching profile:', error)
      return null
    }
    return data
  },
}

// Puzzles API
export const puzzlesApi = {
  getList: async (difficulty?: string) => {
    const params: Record<string, string> = {}
    if (difficulty) params.difficulty = difficulty
    
    return fetchEdgeFunction<{ puzzles: any[] }>('puzzles', { params })
  },
  
  getById: async (id: number) => {
    return fetchEdgeFunction<{ puzzle: any }>(`puzzles/${id}`)
  },
  
  validate: async (id: number, position: number, value: number) => {
    return fetchEdgeFunction<{ isCorrect: boolean }>(`puzzles-validate/${id}`, {
      method: 'POST',
      body: { position, value },
    })
  },
  
  getHint: async (id: number, currentState: string) => {
    return fetchEdgeFunction<{ position: number; value: number } | { message: string }>(
      `puzzles-hint/${id}`,
      {
        method: 'POST',
        body: { currentState },
      }
    )
  },
}

// Progress API
export const progressApi = {
  getAll: async () => {
    return fetchEdgeFunction<{ progress: any[] }>('progress')
  },
  
  getByPuzzle: async (puzzleId: number) => {
    return fetchEdgeFunction<{ progress: any }>(`progress/${puzzleId}`)
  },
  
  save: async (
    puzzleId: number,
    data: {
      currentState: string
      notes?: Record<number, number[]>
      timeSpent?: number
      hintsUsed?: number
      mistakes?: number
    }
  ) => {
    return fetchEdgeFunction<{ progress: any }>(`progress/${puzzleId}`, {
      method: 'PUT',
      body: data,
    })
  },
  
  complete: async (
    puzzleId: number,
    data: {
      currentState: string
      timeSpent: number
      hintsUsed: number
      mistakes: number
    }
  ) => {
    return fetchEdgeFunction<{ progress: any; score: number; message: string }>(
      `progress-complete/${puzzleId}`,
      {
        method: 'POST',
        body: data,
      }
    )
  },
  
  // Sync local progress to server after login
  syncLocalProgress: async (puzzleIds: number[]) => {
    return fetchEdgeFunction<{
      message: string
      results: {
        synced: number[]
        skipped: number[]
        failed: { puzzleId: number; error: string }[]
      }
    }>('progress-sync', {
      method: 'POST',
      body: { puzzleIds },
    })
  },
}

// Leaderboard API
export const leaderboardApi = {
  getGlobal: async (limit?: number, offset?: number) => {
    const params: Record<string, string> = {}
    if (limit) params.limit = String(limit)
    if (offset) params.offset = String(offset)
    
    return fetchEdgeFunction<{ leaderboard: any[] }>('leaderboard/global', { params })
  },
  
  getByPuzzle: async (puzzleId: number, limit?: number) => {
    const params: Record<string, string> = {}
    if (limit) params.limit = String(limit)
    
    return fetchEdgeFunction<{ leaderboard: any[] }>(`leaderboard/puzzle/${puzzleId}`, { params })
  },
  
  getByDifficulty: async (difficulty: string, limit?: number) => {
    const params: Record<string, string> = {}
    if (limit) params.limit = String(limit)
    
    return fetchEdgeFunction<{ leaderboard: any[]; difficulty: string }>(
      `leaderboard/difficulty/${difficulty}`,
      { params }
    )
  },
  
  getMyRanking: async () => {
    return fetchEdgeFunction<{
      globalRank: number | null
      totalScore: number
      gamesCompleted: number
      recentRankings?: any[]
      message?: string
    }>('leaderboard/me')
  },
}
